import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ConnectAccountManagement } from '@stripe/react-connect-js';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '../../../components/ui';
import { FinSkeletonLine } from '../../connect/finance/fin-skeleton';
import FinButton from '../../connect/finance/fin-button';
import COPY from '../../connect/finance/connect-finance-copy';
import connectApi from '../../../services/connectApi';
import ConnectAccountStrip from '../../connect/connect-account-strip';
import ConnectAccountRecord from '../../connect/connect-account-record';
import ConnectOnboarding from '../../connect/connect-onboarding';
import ActionRequiredBanner from '../../connect/action-required-banner';
import { useFinanceSection } from '../section/finance-section-context';
import { CONNECT_DIRECT } from '../../../utils/connectModel';
import FinancePageShell from './finance-page-shell';

// The account itself: what Stripe knows about this business and the people it
// verifies that against. Moved here from Settings -> Payments (spec
// 2026-09-14 §1), where account-sections.js carried this same content as its
// "Account" tab (and its own comment gave the reasoning below) — the component
// choice and the one switch that decides which of onboarding/account-management
// to mount are unchanged.
//
// The bank account appears twice on purpose-by-constraint. Stripe's
// account_management draws its own payout-details section, and the only switch
// for it — external_account_collection — has to carry the same value as
// account_onboarding or Stripe refuses to mint the session at all
// (ConnectAccountSessionPlan has the error verbatim). Onboarding needs it on,
// so account_management keeps it too. Nothing on this page explains that any
// more: the Bank Account sub-menu entry is right there, and a paragraph
// pointing at it read as an apology for a layout the restaurant can see.
//
// `statusLoaded` is not the same fact as `status`: `status` is `null` both
// before the first read and after a read that failed or found no account,
// and collapsing them would mean a restaurant whose account is already
// READY sees the onboarding form for one render on every mount — the
// invitation-to-start flash account-sections.js's own header promised never
// to show.
const BusinessDetails = () => {
  const { model, companyId, status, statusLoaded, instance, sessionFailure, refreshStatus } =
    useFinanceSection();

  // Onboarding changes the account underneath us. `status` above is read
  // once by the section, on mount — this page has no local copy of its own
  // to patch, and must not keep one: an earlier round of this page held its
  // own `refreshed` state here, re-reading status a second time, and left
  // the section's own `hasAccount` (and the Stripe instance it gates
  // downstream) permanently stale for a company that started with no
  // account — `refreshStatus` re-runs the section's one status read instead,
  // so `status`/`instance` above both flow from the same place they always
  // do, and a newly-true `hasAccount` reaches this page as a normal context
  // update rather than a page-local guess.
  //
  // Wrapped in useCallback for the same reason connect-onboarding.js's own
  // comment on `onStatusChange` requires of every caller: it sits in that
  // component's `loadStatus` dependency array, and `loadStatus` is that
  // component's mount effect's only dependency, so a new function identity
  // here on every render would re-run that effect, and re-read status, on
  // every render of this page rather than only when onboarding actually
  // changes something. `refreshStatus` from context is itself stable except
  // when `companyId` changes, so this stays stable too.
  const onStatusChange = useCallback(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Looking is free; changing costs a Stripe sign-in. Stripe's
  // account_management component authenticates the connected account's own
  // user before it will show anything, and that authentication expires — so
  // a restaurant that only wanted to check its address met a sign-in wall on
  // arrival, every time the previous one had lapsed (measured on fe-test,
  // 2026-09-16: the same account gated Account and did not gate Payouts, so
  // the wall belongs to this component rather than to the session). Reading
  // the same facts through our own API asks nobody to sign in. Pressing the
  // button below hands over to the component, which is where Stripe's
  // authentication belongs — at the moment of change.
  const [managing, setManaging] = useState(false);
  const [details, setDetails] = useState({ data: null, loading: true, error: '' });
  // Matches the requestId/mounted idiom the other Finance reads use: a
  // response for a previous company must never write over a newer one's.
  const requestId = useRef(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const isReady = status?.connectStatus === 'READY';

  const loadDetails = useCallback(async () => {
    if (!companyId) return;
    requestId.current += 1;
    const id = requestId.current;
    setDetails((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const data = connectApi.toAccountDetails(await connectApi.getAccountDetails(companyId));
      if (!mounted.current || id !== requestId.current) return;
      if (!data) throw new Error('account-details');
      setDetails({ data, loading: false, error: '' });
    } catch {
      if (!mounted.current || id !== requestId.current) return;
      // An empty record and a refused read look identical on screen, and
      // only one of them is true — the same refusal every other Finance
      // section makes.
      setDetails({
        data: null,
        loading: false,
        error: "We couldn't read your account details from Stripe just now.",
      });
    }
  }, [companyId]);

  // Only once the account is finished. Before that the page is the onboarding
  // form and there is no record to read — asking would spend a Stripe call to
  // be told the same thing `status` already said.
  useEffect(() => {
    if (isReady) loadDetails();
  }, [isReady, loadDetails]);

  // Back from Stripe's component. Re-reads on the way out, so a change made
  // in there is on screen rather than a page-refresh away.
  const onDoneManaging = useCallback(() => {
    setManaging(false);
    loadDetails();
  }, [loadDetails]);

  // `instance` here already carries FinanceSectionInner's own sessionOk gate
  // (finance-section.js sets it to null unless the account exists, the
  // Stripe instance built, and no session error occurred), so this check
  // only needs to add `sessionFailure` for the same precedence
  // account-sections.js settled on: a session that failed wins over an
  // `instance` that merely exists.
  const sessionOk = !!instance && !sessionFailure;

  // Nothing about status is known yet. A skeleton, not a branch: showing
  // onboarding here is the flash this page must never produce, and showing
  // account management on a guess would be worse.
  const accountSkeleton = (
    <div className="space-y-2">
      <FinSkeletonLine className="h-3 w-56" />
      <FinSkeletonLine className="h-3 w-44" />
    </div>
  );

  // Not ready always means the onboarding form, whatever this page's own
  // Stripe session is doing — ConnectOnboarding mints and owns an
  // account-onboarding session entirely on its own, so a session failure
  // here must never block a restaurant who has not finished setup from
  // being able to start.
  // Finished, and the restaurant asked to change something: hand over to
  // Stripe. `sessionOk` still gates the mount for the reason it always did —
  // a component mounted outside a provider whose session failed renders
  // nothing and explains nothing.
  const managementBody = sessionOk ? (
    <ConnectAccountManagement />
  ) : (
    <p className="text-[13.5px] text-fin-muted">
      {COPY.restaurant.sessionFailed}
    </p>
  );

  // Finished, and the restaurant is only looking: our own record. A failed
  // read says so and offers the way back in rather than showing an empty
  // list — and the button in the header is untouched either way, so Stripe
  // is always reachable even when our read is not.
  const recordBody = details.loading && !details.data ? (
    accountSkeleton
  ) : details.error ? (
    // Two ways out of a failed read, because the record's own cards — which
    // now carry the only links to Stripe — are exactly what did not draw.
    // Without the second button a read that failed would also cut the
    // restaurant off from Stripe entirely.
    <div className="flex flex-wrap items-center gap-3">
      <p className="text-[13.5px] text-fin-red">{details.error}</p>
      <FinButton onClick={loadDetails}>Try again</FinButton>
      <FinButton onClick={() => setManaging(true)}>Manage with Stripe</FinButton>
    </div>
  ) : (
    // "Manage bank account" reuses this page's own inline hand-over
    // (spec 2026-09-22 §C review: simpler than opening a second, modal copy
    // of the same `<ConnectAccountManagement/>` this page already mounts
    // inline for "Manage business details" — either way the reader lands on
    // Stripe's own account form, in place, with the header's Done button as
    // the way back).
    <ConnectAccountRecord details={details.data} onManage={() => setManaging(true)} onManageBank={() => setManaging(true)} />
  );

  // Only rendered at the site below, in the else of `statusLoaded && isReady
  // && !managing` — so a fourth branch reaching `recordBody` again here
  // could never actually evaluate (final wave item 17).
  const accountBody = !statusLoaded
    ? accountSkeleton
    : !isReady
      ? <ConnectOnboarding companyId={companyId} onStatusChange={onStatusChange} />
      : managementBody;

  // Only a way back, never a way in. Each card in the record below carries
  // its own "Manage …" button, beside the thing it is about, which is where
  // a restaurant is looking when it decides to change something — a second
  // entry point up here only competed with those. What the header must still
  // hold is the exit: once Stripe's component is mounted it fills the page,
  // and without this there is no way back to the record short of leaving for
  // another screen.
  // Overview's account strip Manage lands on #payout-details (owner,
  // 2026-09-23): once the record has drawn, bring that card into view rather
  // than leaving the reader at the top of a long page.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== '#payout-details' || !details.data) return;
    document.getElementById('payout-details')?.scrollIntoView?.({ block: 'start' });
  }, [hash, details.data]);

  const headerAction =
    statusLoaded && isReady && managing ? <FinButton onClick={onDoneManaging}>Done</FinButton> : null;

  return (
    <FinancePageShell column="reading">
      <PageHeader
        title="Business Details"
        description="Your legal business information and who it is verified against."
        actions={headerAction}
      />
      {/* Above the strip, because it is the one fact that needs acting on
          rather than reading (spec 2026-09-22 §9). Hidden when nothing is
          due; "Review requirements" is the same hand-over the record's own
          Manage buttons make — but only once there is a record to hand over
          from. A Restricted account renders onboarding, not the record, so
          `managing` has nothing to flip there: `onReview` is left undefined
          and the banner hides its own button rather than leaving a press
          with nowhere to go (task 11 review ruling, 2026-09-22). */}
      {statusLoaded ? (
        <ActionRequiredBanner status={status} onReview={isReady ? () => setManaging(true) : undefined} />
      ) : null}
      {/* The three status cards come back with Activate (owner,
          2026-09-25): while a company is onboarding, this page is the form
          and nothing it has not earned yet. */}
      {statusLoaded && model === CONNECT_DIRECT ? <ConnectAccountStrip status={status} /> : null}
      {/* Stripe's own components fill the 920px reading column, like our
          cards: the 3xl box they used to sit in left them visibly narrower
          than the record above them (spec 2026-09-22 §9). */}
      {statusLoaded && isReady && !managing ? recordBody : accountBody}
    </FinancePageShell>
  );
};

export default BusinessDetails;
